import './SubworkflowEditor.css';

import { Button, Dropdown, Modal } from 'react-bootstrap';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import ReactFlow, { ReactFlowProvider } from 'react-flow-renderer';

import { AbNodeEditor } from '../modals/NodeEditors/AbNodeEditor';
import { BranchingNode } from './CustomNodes/CustomBranchingNode';
import { CreateAbTest } from '../modals/CreateAbTest';
import CustomEdge from './CustomEdges/CustomEdge';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import { MachineTranslationEditor } from '../modals/NodeEditors/MachineTranslationEditor';
import { TerminologyBaseEditor } from '../modals/NodeEditors/TerminologyBaseEditor';
import { TranslationMemoryEditor } from '../modals/NodeEditors/TranslationMemoryEditor';
import { WORKFLOW_STEP_TYPES } from '../../../../global/constants';
import { WorkflowNode } from './CustomNodes/CustomWorkflowNode';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import { TenantIdContext, TenantMetadataContext } from '../../../../components/app/App';
import ConfirmationDialog from '../../../../global/confirmation-dialog/ConfirmationDialog';
import Alert from '@amzn/meridian/alert';
import Link from '@amzn/meridian/link';
import { AbMetricsLookupViewer } from '../modals/NodeEditors/AbMetricsLookupViewer';
import { initiateAbMetricsLookup } from '../../services/api-calls';
import { PromotionButtons } from './PromotionButtons';
import TestingModal from '../testing/TestingModal';

const NODE_TYPES = {
  BranchingNode: BranchingNode,
  WorkflowNode: WorkflowNode,
};

const EDGE_TYPES = {
  CustomEdge: CustomEdge,
};

const BASE_HEIGHT = 20;
const BRANCH_HEIGHT = 120;
const GRAPH_SCALE_FACTOR = 0.7;

export const SubworkflowEditor = ({
  workflowId,
  subWorkflow,
  workflowGraph,
  onDeleteSubworkflow,
  onGraphUpdated,
  onPromoteToProd,
  editable,
  onSave,
}) => {
  const [edgeSelectionOptions, setEdgeSelectionOptions] = useState([]);
  const [addingPostMerge, setAddingPostMerge] = useState(false);
  const [currentlyAddingEdge, setCurrentlyAddingEdge] = useState(null);
  const [abTestEndOptions, setAbTestEndOptions] = useState([]);
  const [abTestStartNode, setAbTestStartNode] = useState(-1);
  const [completingAbTest, setCompletingAbTest] = useState(false);
  const [newAbTestControl, setNewAbTestControl] = useState('');
  const [editingNode, setEditingNode] = useState(null);
  const [graphHeight, setGraphHeight] = useState('200px');
  const [graphWidth, setGraphWidth] = useState('100%');
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false);
  const [deleteConfirmationCallback, setDeleteConfirmationCallback] = useState(() => {});
  const tenantId = useContext(TenantIdContext);
  const tenantMetaData = useContext(TenantMetadataContext);
  const [metricsRoleArn, setMetricsRoleArn] = useState('');
  const [metricsNode, setMetricsNode] = useState(null);
  const [isMetricsLookupInitiated, setIsMetricsLookupInitiated] = useState(false);
  const [isMetricsRequesterVerified, setIsMetricsRequesterVerified] = useState(true);
  const [isTesting, setIsTesting] = useState(false);

  const updateGraphHeight = useCallback(() => {
    if (!workflowGraph) {
      return;
    }
    const height = BASE_HEIGHT + BRANCH_HEIGHT * workflowGraph.getNumberOfBranches();
    setGraphHeight(height + 'px');
  }, [workflowGraph]);

  const updateGraphWidth = useCallback(() => {
    if (!workflowGraph) {
      return;
    }
    setGraphWidth(workflowGraph.width * GRAPH_SCALE_FACTOR + 'px');
  }, [workflowGraph]);

  const getFlowElements = useCallback(() => {
    if (!workflowGraph) {
      return [];
    }

    return workflowGraph.asFlowElements(editable);
  }, [workflowGraph, editable]);

  const addNewNodeCallback = useCallback(
    (e, isPostMerge) => {
      e.stopPropagation();
      e.persist();
      setAddingPostMerge(isPostMerge);
      setEdgeSelectionOptions(workflowGraph.getNodesCanAddAtEdge(e.target.id, isPostMerge));
      setCurrentlyAddingEdge(e);
    },
    [workflowGraph]
  );

  const removeNodeCallback = useCallback(
    nodeId => {
      workflowGraph.removeNode(parseInt(nodeId));
      onGraphUpdated(subWorkflow.sourceLocale, subWorkflow.targetLocale, workflowGraph);
      subWorkflow.isTested = false;
      setFlowElements(getFlowElements());
      updateGraphWidth();
    },
    [workflowGraph, onGraphUpdated, subWorkflow, getFlowElements, updateGraphWidth]
  );

  const editNodeCallback = useCallback(nodeId => {
    setEditingNode(parseInt(nodeId, 10));
  }, []);

  const addAbTest = useCallback(
    (fromNode, toNode, metricsRoleArn) => {
      workflowGraph.addAbTest(fromNode, toNode, metricsRoleArn);
      onGraphUpdated(subWorkflow.sourceLocale, subWorkflow.targetLocale, workflowGraph);
      subWorkflow.isTested = false;
      setFlowElements(getFlowElements());
      updateGraphHeight();
      updateGraphWidth();
    },
    [workflowGraph, onGraphUpdated, subWorkflow, getFlowElements, updateGraphHeight, updateGraphWidth]
  );

  const addAbTestCallback = useCallback(
    nodeId => {
      nodeId = parseInt(nodeId, 10);
      const availableForControl = workflowGraph.getNodesForAbTestEndOptions(nodeId);
      if (availableForControl.length === 1) {
        const endOfTest = workflowGraph.getNextNodeId(nodeId);
        addAbTest(nodeId, endOfTest, metricsRoleArn);
      }
      setAbTestStartNode(nodeId);
      setAbTestEndOptions(availableForControl);
    },
    [workflowGraph, addAbTest, metricsRoleArn]
  );

  const addAbTreatmentGroupCallback = useCallback(() => {
    workflowGraph.addTreatmentBranchForAbTest();
    onGraphUpdated(subWorkflow.sourceLocale, subWorkflow.targetLocale, workflowGraph);
    subWorkflow.isTested = false;
    setFlowElements(getFlowElements());
    updateGraphHeight();
  }, [workflowGraph, onGraphUpdated, subWorkflow, updateGraphHeight, getFlowElements]);

  const handleNewNodeSelected = eventKey => {
    if (addingPostMerge) {
      workflowGraph.addNodeAfterAb(eventKey);
    } else {
      workflowGraph.addNodeAfter(currentlyAddingEdge.target.id, eventKey);
    }
    onGraphUpdated(subWorkflow.sourceLocale, subWorkflow.targetLocale, workflowGraph);
    subWorkflow.isTested = false;
    setFlowElements(getFlowElements());
    hideEdgeSelectionMenu();
    updateGraphWidth();
  };

  const hideEdgeSelectionMenu = () => {
    setEdgeSelectionOptions([]);
    setCurrentlyAddingEdge(null);
    setAddingPostMerge(false);
  };

  const ElementsAvailableToAdd = () => {
    if (edgeSelectionOptions.length === 0) {
      return null;
    }
    const listGroupStyles = {
      left: currentlyAddingEdge.clientX,
      top: currentlyAddingEdge.clientY,
    };
    return (
      <div style={listGroupStyles} className="position-absolute force-to-top">
        <Dropdown onSelect={handleNewNodeSelected}>
          {edgeSelectionOptions.map(selectionOption => (
            <Dropdown.Item key={selectionOption} eventKey={selectionOption} className="border border-info bg-white">
              {workflowGraph.getDisplayName(selectionOption)}
            </Dropdown.Item>
          ))}
        </Dropdown>
      </div>
    );
  };

  const ControlGroupPopup = () => {
    if (abTestEndOptions.length === 0) {
      return <></>;
    }

    return (
      <CreateAbTest
        onAccept={handleControlGroupPopupAccept}
        onCancel={handleControlGroupPopupCancel}
        abTestEndOptions={abTestEndOptions}
      />
    );
  };

  const handleControlGroupPopupCancel = () => {
    _clearControlGroupPopup();
  };

  const handleControlGroupPopupAccept = useCallback(
    selectedValue => {
      if (selectedValue) {
        addAbTest(abTestStartNode, selectedValue, metricsRoleArn);
      }
      _clearControlGroupPopup();
    },
    [addAbTest, abTestStartNode, metricsRoleArn]
  );

  const _clearControlGroupPopup = () => {
    setAbTestStartNode(-1);
    setAbTestEndOptions([]);
  };

  const completeAbTestCallback = useCallback(() => {
    setNewAbTestControl(workflowGraph.getControlBranchStart());
    setCompletingAbTest(true);
  }, [workflowGraph]);

  const lookupAbMetricsCallback = useCallback(nodeId => {
    setMetricsNode(parseInt(nodeId, 10));
  }, []);

  useEffect(() => {
    if (tenantMetaData === {}) {
      return;
    }
    const metricsRoleArn = tenantMetaData.resourceData?.metricsRoleArn;
    setMetricsRoleArn(metricsRoleArn);
  }, [tenantMetaData]);

  useEffect(() => {
    if (!workflowGraph) {
      return;
    }
    workflowGraph.callbacks = {
      addNewNode: addNewNodeCallback,
      removeNode: removeNodeCallback,
      editNode: editNodeCallback,
      addAbTreatmentGroup: addAbTreatmentGroupCallback,
      addAbTest: addAbTestCallback,
      completeAbTest: completeAbTestCallback,
      lookupAbMetrics: lookupAbMetricsCallback,
    };
  }, [
    workflowGraph,
    addNewNodeCallback,
    removeNodeCallback,
    editNodeCallback,
    addAbTreatmentGroupCallback,
    addAbTestCallback,
    completeAbTestCallback,
    lookupAbMetricsCallback,
  ]);

  const CompleteAbPopup = () => {
    if (!completingAbTest) {
      return <></>;
    }

    const branches = workflowGraph.getAbTreatmentBranches();
    const options = [];
    branches.forEach((treatmentName, treatmentStartId) => {
      options.push(
        <div key={treatmentStartId} className="radio">
          <label>
            <input
              type="radio"
              name="newControl"
              className="mr-3"
              value={treatmentStartId}
              checked={newAbTestControl === treatmentStartId}
              onChange={handleAbControlSelectionChanged}
              key={treatmentStartId}
            />
            {treatmentName}
          </label>
        </div>
      );
    });

    return (
      <Modal show animation={false}>
        <Modal.Header>
          <Modal.Title>Create A/B Test</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>Select the treatment group that should be maintained as the new control group:</div>
          {options}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCompleteAbTestPopupCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCompleteAbTestPopupAccept}>
            Complete A/B Test
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const handleCompleteAbTestPopupCancel = () => {
    _clearCompleteAbTest();
  };

  const handleCompleteAbTestPopupAccept = () => {
    workflowGraph.resolveAbTest(newAbTestControl);
    onGraphUpdated(subWorkflow.sourceLocale, subWorkflow.targetLocale, workflowGraph);
    setFlowElements(getFlowElements());
    updateGraphHeight();
    updateGraphWidth();
    _clearCompleteAbTest();
  };

  const _clearCompleteAbTest = () => {
    setNewAbTestControl('');
    setCompletingAbTest(false);
  };

  const handleAbControlSelectionChanged = event => {
    if (event.target.checked) {
      setNewAbTestControl(parseInt(event.target.value, 10));
    }
  };

  const AbMetricsPopup = () => {
    if (metricsNode === null) {
      return null;
    }
    const node = workflowGraph.getNode(metricsNode);
    return (
      <AbMetricsLookupViewer
        nodeData={node}
        onCancel={handleLookupMetricsCancel}
        onConfirm={handleLookupMetricsConfirm}
        isConfirmed={isMetricsLookupInitiated}
        isVerified={isMetricsRequesterVerified}
      />
    );
  };

  const handleLookupMetricsConfirm = () => {
    if (metricsNode === null) {
      return null;
    }
    const abNode = workflowGraph.getNode(metricsNode);
    initiateAbMetricsLookup(
      tenantId,
      workflowId,
      abNode.parameters.id,
      metricsRoleArn,
      isVerified => {
        setIsMetricsLookupInitiated(true);
        setIsMetricsRequesterVerified(isVerified);
      },
      handleLookupMetricsCancel
    );
  };

  const handleLookupMetricsCancel = () => {
    setMetricsNode(null);
    setIsMetricsLookupInitiated(false);
  };

  const EditNodePopup = () => {
    if (editingNode === null) {
      return null;
    }

    const node = workflowGraph.getNode(editingNode);
    switch (node.type) {
      case WORKFLOW_STEP_TYPES.AB:
        return <AbNodeEditor nodeData={node} onCancel={handleEditNodeCancel} onAccept={handleEditAbAccept} />;
      case WORKFLOW_STEP_TYPES.MachineTranslation:
        return (
          <MachineTranslationEditor
            subWorkflow={subWorkflow}
            node={node}
            onAccept={handleEditNodeAccept}
            onCancel={handleEditNodeCancel}
          />
        );
      case WORKFLOW_STEP_TYPES.TerminologyBase:
        return (
          <TerminologyBaseEditor
            subWorkflow={subWorkflow}
            node={node}
            onAccept={handleEditNodeAccept}
            onCancel={handleEditNodeCancel}
          />
        );
      case WORKFLOW_STEP_TYPES.TranslationMemory:
        return (
          <TranslationMemoryEditor
            subWorkflow={subWorkflow}
            node={node}
            onAccept={handleEditNodeAccept}
            onCancel={handleEditNodeCancel}
          />
        );
      default:
        return null;
    }
  };

  const handleEditNodeCancel = () => {
    setEditingNode(null);
  };

  const handleEditNodeAccept = () => {
    onGraphUpdated(subWorkflow.sourceLocale, subWorkflow.targetLocale, workflowGraph);
    subWorkflow.isTested = false;
    workflowGraph.tryMarkBranchUntested(editingNode);
    setEditingNode(null);
  };

  const handleEditAbAccept = allocationData => {
    const abNode = workflowGraph.getNode(editingNode);
    const numberOfBuckets = abNode.parameters.buckets;

    let startingBucket = 0;
    for (const [index, allocation] of abNode.parameters.allocations.entries()) {
      const percent = allocationData[index];
      const points = (percent / 100) * numberOfBuckets;
      allocation.start = startingBucket;
      allocation.end = startingBucket + points - 1;
      startingBucket += points;
    }
    //TODO: remove after backfill all metricsRoleArn in workflows that has ongoing AB test
    abNode.parameters.metricsRoleArn = metricsRoleArn;
    onGraphUpdated(subWorkflow.sourceLocale, subWorkflow.targetLocale, workflowGraph);

    setEditingNode(null);
  };

  const handleTestingLinkClicked = useCallback(() => {
    onSave();
    setIsTesting(true);
  }, [onSave]);

  const handleTestSuccess = useCallback(() => {
    setIsTesting(false);
  }, []);

  const handleTestCancel = useCallback(() => {
    setIsTesting(false);
  }, []);

  const deleteConfirmation = () => {
    setDeleteConfirmationCallback(() => () => onDeleteSubworkflow(subWorkflow.sourceLocale, subWorkflow.targetLocale));
    setIsDeleteConfirmationDialogOpen(true);
  };

  const reactFlowLoadedHandler = reactFlowInstance => {
    reactFlowInstance.setTransform({ x: 0, y: 0, zoom: GRAPH_SCALE_FACTOR });
  };

  useEffect(() => {
    setFlowElements(getFlowElements());
  }, [workflowGraph, getFlowElements]);

  useEffect(() => {
    updateGraphHeight();
  }, [workflowGraph, updateGraphHeight]);

  useEffect(() => {
    updateGraphWidth();
  }, [workflowGraph, updateGraphWidth]);

  useEffect(() => {
    window.onresize = updateGraphWidth;
  }, [updateGraphWidth]);

  const [flowElements, setFlowElements] = useState([]);

  return (
    <div>
      <Heading level={2} className="align-middle">
        {`${subWorkflow.sourceLocale}:${subWorkflow.targetLocale}`}
        <div className="float-right">
          <PromotionButtons
            data={subWorkflow}
            onPromoteToProduction={onPromoteToProd}
            onTestingLinkClicked={handleTestingLinkClicked}
            editable={editable}
          />
          {editable ? (
            <Button className="ml-2" variant="secondary" onClick={deleteConfirmation} size="sm">
              <Icon tokens={trashTokens} /> Delete Subworkflow
            </Button>
          ) : null}
        </div>
        {editable && !subWorkflow.isTested ? (
          <div className="ml-1 mt-2">
            <Alert size="medium" type="warning" className="display-inline">
              This subworkflow has untested changes. You must test these changes before promoting it to production.{' '}
              <Link onClick={() => handleTestingLinkClicked(subWorkflow)}>Test it now.</Link>
            </Alert>
          </div>
        ) : null}
      </Heading>
      <div className="mt-2 mb-2 w-100 border-bottom overflow-x-scroll">
        <div style={{ height: graphHeight, width: graphWidth }}>
          <ReactFlowProvider>
            <ReactFlow
              elements={flowElements}
              nodeTypes={NODE_TYPES}
              nodesConnectable={false}
              nodesDraggable={false}
              edgeTypes={EDGE_TYPES}
              onClick={hideEdgeSelectionMenu}
              onLoad={reactFlowLoadedHandler}
              preventScrolling={false}
              zoomOnScroll={false}
            ></ReactFlow>
          </ReactFlowProvider>

          <ElementsAvailableToAdd />
          <ControlGroupPopup />
          <EditNodePopup />
          <CompleteAbPopup />
          <AbMetricsPopup />
          <ConfirmationDialog
            isOpen={isDeleteConfirmationDialogOpen}
            setIsOpen={setIsDeleteConfirmationDialogOpen}
            text={'Are you sure you would like to remove this subworkflow?'}
            onConfirm={deleteConfirmationCallback}
          />
          <TestingModal
            isOpen={isTesting}
            onSuccess={handleTestSuccess}
            onCancel={handleTestCancel}
            workflowId={workflowId}
            sourceLocale={subWorkflow.sourceLocale}
            targetLocale={subWorkflow.targetLocale}
            useBuildVersion
          />
        </div>
      </div>
    </div>
  );
};
